import { Box, Flex, Input, Text } from '@chakra-ui/react'
import ReactSelect from "react-select";
import { MultiSelectStylesForDashboards } from 'src/pages-admin/Cms/utils/multiSelectStyles';
import { useEffect, useMemo, useState } from 'react';
import { Option } from './ReportContent';
import { useTranslation } from 'react-i18next';
import { DateRange } from './DateRangeFilter';

interface SelectDateFilterProps {
  dateFilterSelected: Option | undefined,
  setDateFilterSelected: (value: Option | undefined) => void,
  dateRange: DateRange,
  setDateRange: (value: DateRange) => void,
  companyId?: number
}

enum DateFilterEnum {
  last_day = 'last_day',
  last_7_days = 'last_7_days',
  last_30_days = 'last_30_days',
  last_year = 'last_year',
  custom_date_range = 'custom_date_range',
}

export const SelectDateFilter = ({
  dateFilterSelected,
  setDateFilterSelected,
  dateRange,
  setDateRange,
  companyId = 2
}: SelectDateFilterProps) => {
  const [t] = useTranslation()
  const [startDate, setStartDate] = useState<string>(dateRange.start_date || '')
  const [endDate, setEndDate] = useState<string>(dateRange.end_date || '')
  const [showDateRangeInputs, setShowDateRangeInputs] = useState<boolean>(false)

  const options: Option[] = useMemo(() => {
    const options: { [key in DateFilterEnum]: string } = {
      [DateFilterEnum.last_day]: t('admin.reports.last_day'),
      [DateFilterEnum.last_7_days]: t('admin.reports.last_7_days'),
      [DateFilterEnum.last_30_days]: t('admin.reports.last_30_days'),
      [DateFilterEnum.last_year]: t('admin.reports.last_year'),
      [DateFilterEnum.custom_date_range]: t('admin.reports.use_date_range'),
    }

    return Object.entries(options).map(([key, value]) => ({ value: key, label: value }))
  }, [t])

  useEffect(() => {
    setShowDateRangeInputs(dateFilterSelected?.value === DateFilterEnum.custom_date_range)
  }, [dateFilterSelected])

  const handleDateChange = (field: 'start_date' | 'end_date', value: string) => {
    if (field === 'start_date') {
      setStartDate(value)
      setDateRange({
        ...dateRange,
        start_date: value,
        companyId
      })
    } else {
      setEndDate(value)
      setDateRange({
        ...dateRange,
        end_date: value,
        companyId
      })
    }
  }

  function handleSelectChange(newValue: unknown) {
    const selectedOption = newValue as Option | null;
    const isCustomRange = selectedOption?.value === DateFilterEnum.custom_date_range;

    if (!isCustomRange) {
      setDateRange({});
    }

    setDateFilterSelected(selectedOption || undefined);
    setShowDateRangeInputs(isCustomRange);
  }

  return (
    <Flex direction='column' flex={1} minW="250px" maxW="300px">
      <ReactSelect
        isSearchable
        isClearable
        styles={MultiSelectStylesForDashboards}
        placeholder={t('admin.reports.select_date')}
        value={dateFilterSelected}
        options={options}
        onChange={handleSelectChange}
      />

      {showDateRangeInputs && (
        <Box mt={2}>
          <Flex direction='row' gap={2} alignItems="flex-end">
            <Flex direction='column' flex={1}>
              <Text fontWeight={600} fontSize="11px" mb={1} color="#313E4C">
                {t('admin.reports.start_date')}
              </Text>
              <Input
                size="sm"
                type="date"
                value={startDate}
                onChange={(e) => handleDateChange('start_date', e.target.value)}
                borderRadius="8px"
                height="30px"
                pl={1.5}
                pr={1}
                fontSize="12px"
                _focus={{
                  borderColor: '#009028',
                  boxShadow: '0 0 0 1px #009028',
                }}
              />
            </Flex>
            <Flex direction='column' flex={1}>
              <Text fontWeight={600} fontSize="11px" mb={1} color="#313E4C">
                {t('admin.reports.end_date')}
              </Text>
              <Input
                size="sm"
                type="date"
                value={endDate}
                min={startDate}
                onChange={(e) => handleDateChange('end_date', e.target.value)}
                borderRadius="8px"
                height="30px"
                pl={1.5}
                pr={1}
                fontSize="12px"
                _focus={{
                  borderColor: '#009028',
                  boxShadow: '0 0 0 1px #009028',
                }}
              />
            </Flex>
          </Flex>
        </Box>
      )}
    </Flex>
  )
}
